import React, { useEffect, useState } from 'react';
import { ConfigProvider, DatePicker, Input, InputNumber, Button, Form, Typography, Space, Select, Spin, Switch, message } from 'antd';
import { NotificationOutlined, CalendarOutlined, HourglassOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNotificationContent } from '../../contexts/notificationContent';
import 'antd/dist/reset.css';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

function formatDate(isoDate) {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatContentWithLinks(content) {
    // Convert email addresses into <a> tags
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    content = content.replace(emailRegex, '<a style="color: #27c456;" href="mailto:$1">$1</a>');

    // Convert URLs into <a> tags (e.g., https://www.example.com)
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    content = content.replace(urlRegex, '<a style="color: #27c456;" href="$1" target="_blank">$1</a>');

    return content;
}


export default function Notification() {
    const { notification, isLoading, addOrUpdateNotification, deleteNotification, toggleNotification } = useNotificationContent();
    const [isNotificationEnabled, setIsNotificationEnabled] = useState(notification?.enabled ?? false);

    const [form] = Form.useForm();
    const [notificationData, setNotificationData] = useState(notification ?? null);

    useEffect(() => {
        if (notification) {
            setNotificationData(notification);
            setIsNotificationEnabled(notification?.enabled || false);
        } else {
            setNotificationData(null);
        }
    }, [notification])


    const onFinish = async (values) => {
        const { content, range, interval } = values;
        const formattedData = {
            content,
            fromDate: range[0].toISOString(),
            toDate: range[1].toISOString(),
            interval,
            enabled: isNotificationEnabled
        };
        await addOrUpdateNotification(formattedData);
        // setNotificationData(formattedData);
        form.resetFields();
        console.log('Notification Data:', formattedData);
    };

    const handleEdit = () => {
        const startDate = dayjs(notificationData.fromDate);
        const endDate = dayjs(notificationData.toDate);

        if (!startDate.isValid() || !endDate.isValid()) {
            console.error('Invalid date format detected:', { startDate, endDate });
            return;
        }

        form.setFieldsValue({
            content: notificationData.content,
            range: [startDate, endDate],
            interval: notificationData.interval,
        });
    };

    const handleDelete = async () => {
        await deleteNotification(notification.id);
        setNotificationData(null);
    };

    const handleToggle = async (checked) => {
        try {
            if (!notification) {
                message.error('Erstellen Sie zunächst eine Benachrichtigung');
                return;
            }
            await toggleNotification(notification.id)
            setIsNotificationEnabled(checked);
        } catch (error) {
            console.error('Error toggling notification:', error);
        }
    };

    return (
        <div style={{
            position: 'relative',
            padding: '24px',
            backgroundColor: 'rgb(28, 28, 28, 0.95)',
            borderRadius: 5,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '20px'
        }}>
            <img style={{
                width: '100%',
                objectFit: 'contain',
                position: 'fixed',
                zIndex: -1
            }} src="https://cdn.prod.website-files.com/65f03c978efea1c906592c94/65f41d5f2ba41fbbe6c5514c_AdobeStock_546815906_gruen_klein_bearbeitet_002.webp" />
            <ConfigProvider
                theme={{
                    token: {
                        colorBgBase: '#1a1a1a',
                        colorTextBase: '#ffffff',
                        colorPrimary: '#27c456',
                        colorBorder: '#3a3a3a',
                        colorBgContainer: '#333333', // Dropdown background color
                        colorTextPlaceholder: '#bbbbbb', // Placeholder text color
                        colorBgElevated: '#1a1a1a', // Dark background for dropdown items
                        colorText: '#fff', // Text color for dropdown items
                    },
                }}
            >
                <div>
                    <h2 style={{ color: '#ffffff' }}>Benachrichtigungseinstellungen</h2>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        marginTop: '10px',
                        gap: '20px'
                    }}>
                        <Text style={{ color: '#fff' }}>
                            {isNotificationEnabled ? 'Benachrichtigung aktiviert' : 'Benachrichtigung deaktiviert'}
                        </Text>
                        <Switch
                            loading={isLoading}
                            checked={isNotificationEnabled}
                            onChange={handleToggle}
                        />
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                    >
                        <Form.Item
                            label="Benachrichtigungsinhalt"
                            name="content"
                            rules={[{ required: true, message: 'Bitte geben Sie den Benachrichtigungsinhalt ein!' }]}
                        >
                            <Input.TextArea placeholder="Benachrichtigungsinhalt eingeben" rows={4} />
                        </Form.Item>

                        <Form.Item
                            label="Benachrichtigungszeitraum"
                            name="range"
                            rules={[{ required: true, message: 'Bitte wählen Sie den Zeitraum aus!' }]}
                        >
                            <RangePicker
                                placeholder={['Startdatum', 'Enddatum']}
                                format="YYYY-MM-DD"
                                style={{ width: '100%' }}
                                dropdownClassName="custom-range-picker-dropdown"
                                onCalendarChange={(dates) => {
                                    if (dates && dates.length === 1) {
                                        form.setFieldsValue({ range: [dates[0], null] });
                                    } else if (dates && dates.length === 2) {
                                        form.setFieldsValue({ range: dates });
                                    }
                                }}
                                popupStyle={{
                                    backgroundColor: '#1a1a1a',
                                    color: '#ffffff',
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Intervall (Stunden)"
                            name="interval"
                            rules={[
                                { required: true, message: 'Bitte geben Sie das Intervall in Stunden ein!' },
                                { type: 'number', min: 1, message: 'Das Intervall muss mindestens 1 Stunde betragen!' },
                            ]}
                        >
                            <Select
                                popupClassName="custom-popup-select"
                                placeholder="Intervall in Stunden auswählen" style={{ width: '100%' }}>
                                <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value={24}>24 Stunden</Select.Option>
                                <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value={48}>48 Stunden</Select.Option>
                                <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value={72}>72 Stunden</Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.Item>
                            <Button loading={isLoading} type="primary" htmlType="submit" block style={{
                                color: '#000',
                                border: 'none'
                            }}>
                                Benachrichtigung speichern
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </ConfigProvider>
            <div style={{
                marginTop: '24px',
                color: '#ffffff',
                backgroundColor: 'rgb(0, 0, 0, 0.5)',
                padding: '16px',
                borderRadius: '8px',
                position: 'relative'
            }}>
                <Spin spinning={isLoading} style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                }} />
                {
                    notificationData && (
                        <>
                            <Title level={3} style={{ color: '#ffffff' }}>Gespeicherte Benachrichtigung</Title>
                            <Space direction="vertical" style={{ display: 'flex', color: '#ffffff' }}>
                                <Text style={{
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '5px'
                                }}><NotificationOutlined style={{ marginRight: '8px', marginTop: 5 }} /> <strong style={{
                                    whiteSpace: 'nowrap'
                                }}>Inhalt:</strong> <span dangerouslySetInnerHTML={{
                                    __html: formatContentWithLinks(notificationData.content.replace(/\n/g, '<br />'))
                                    // __html: notificationData.content.replace(/\n/g, '<br />')
                                }} /></Text>
                                <Text style={{ color: '#fff' }}><CalendarOutlined style={{ marginRight: '8px' }} /> <strong>Von:</strong> {formatDate(notificationData.fromDate)}</Text>
                                <Text style={{ color: '#fff' }}><CalendarOutlined style={{ marginRight: '8px' }} /> <strong>Bis:</strong> {formatDate(notificationData.toDate)}</Text>
                                <Text style={{ color: '#fff' }}><HourglassOutlined style={{ marginRight: '8px' }} /> <strong>Intervall:</strong> {notificationData.interval} Stunden</Text>
                            </Space>
                            <Space style={{
                                marginTop: '16px',
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}>
                                <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    style={{
                                        backgroundColor: '#1890ff',
                                        color: '#fff'
                                    }}
                                >
                                    Editieren
                                </Button>
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    style={{
                                        backgroundColor: 'red',
                                        color: '#fff'
                                    }}
                                >
                                    Löschen
                                </Button>
                            </Space>
                        </>
                    )}
            </div>
        </div>
    );
}